import React from "react";
import ReactDOM from "react-dom";

type PortalTooltipProps = {
  active?: boolean;
  payload?: any[];
  label?: string;
  coordinate?: { x: number; y: number };
  viewBox?: { x: number; y: number; width: number; height: number };
  formatter?: (value: any, name: string, props: any) => any;
};

const PortalTooltip: React.FC<PortalTooltipProps> = ({
  active,
  payload,
  label,
  coordinate,
  formatter,
}) => {
  const chartRef = React.useRef<DOMRect | null>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!active || !coordinate) {
      chartRef.current = null;
      return;
    }

    // Find the chart wrapper by checking all wrappers and picking the one
    // where the tooltip coordinate falls within its bounds
    const wrappers = document.querySelectorAll(".recharts-wrapper");
    wrappers.forEach((w) => {
      const rect = w.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        // Store the most recently found visible wrapper
        // Recharts only activates tooltip on the hovered chart,
        // so we find the wrapper by checking if it contains a hovered SVG
        const svg = w.querySelector("svg");
        if (svg && svg.matches(":hover")) {
          chartRef.current = rect;
        }
      }
    });

    // Fallback: if no :hover match, try matching by page position
    if (!chartRef.current) {
      wrappers.forEach((w) => {
        const rect = w.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          chartRef.current = rect;
        }
      });
    }
  }, [active, coordinate]);

  if (!active || !payload?.length || !coordinate || !chartRef.current) return null;

  let left = chartRef.current.left + coordinate.x + 10;
  let top = chartRef.current.top + coordinate.y - 10;

  // Clamp to viewport using actual tooltip size
  const tooltipEl = tooltipRef.current;
  if (tooltipEl) {
    const tw = tooltipEl.offsetWidth;
    const th = tooltipEl.offsetHeight;
    if (left + tw > window.innerWidth - 8) {
      left = chartRef.current.left + coordinate.x - tw - 10;
    }
    if (top + th > window.innerHeight - 8) {
      top = window.innerHeight - th - 8;
    }
    if (left < 8) left = 8;
    if (top < 8) top = 8;
  }

  const content = (
    <div
      ref={tooltipRef}
      style={{
        position: "fixed",
        left,
        top,
        backgroundColor: "#fff",
        border: "1px solid #e3e6f0",
        borderRadius: "6px",
        padding: "8px 12px",
        fontSize: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        zIndex: 10000,
        pointerEvents: "none",
        whiteSpace: "nowrap",
      }}
    >
      {label && <div style={{ fontWeight: 600, marginBottom: "4px", color: "#3a4550" }}>{label}</div>}
      {payload.map((entry: any, i: number) => {
        let displayValue = entry.value;
        if (formatter) {
          const formatted = formatter(entry.value, entry.name, entry);
          if (Array.isArray(formatted)) {
            displayValue = formatted[0];
          } else {
            displayValue = formatted;
          }
        }
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "2px 0" }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: entry.color, display: "inline-block" }} />
            <span style={{ color: "#666" }}>{entry.name}:</span>
            <span style={{ fontWeight: 600, color: "#3a4550" }}>{displayValue}</span>
          </div>
        );
      })}
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default PortalTooltip;
